(() => {
  const configElement = document.getElementById('BQGiftConfig');
  if (!configElement || window.BQGiftCart) return;
  const config = JSON.parse(configElement.textContent);
  if (!config.enabled && !config.hasGift) return;
  const root = config.root.endsWith('/') ? config.root : `${config.root}/`;
  const reserved = new Set(config.enabled
    ? config.tiers.filter((tier) => tier.variantId && tier.price === 0).map((tier) => String(tier.variantId))
    : []);
  const tiers = config.enabled ? config.tiers.filter((tier) =>
    tier.enabled && tier.variantId && tier.available && tier.price === 0 && tier.minimum > 0
  ).sort((a, b) => b.minimum - a.minimum || b.tier - a.tier) : [];
  let queue = Promise.resolve();
  let checkoutBusy = false;
  let renderedSignature = config.cartSignature ? JSON.stringify(config.cartSignature) : '';
  const signature = (cart) => JSON.stringify([cart.currency, cart.total_price, cart.items.map((item) => [item.key, item.quantity, item.final_line_price])]);

  function report(error) {
    const status = document.querySelector('[data-bq-gift-status]');
    if (status) {
      status.hidden = !error;
      status.textContent = error ? config.error : '';
    }
    if (error) console.error('BQ gift cart:', error);
  }

  function enqueue(operation) {
    const result = queue.then(operation);
    queue = result.catch(() => {});
    return result;
  }

  async function json(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.description || data.message || config.error);
      error.status = response.status;
      error.responseJSON = data;
      throw error;
    }
    return data;
  }

  function post(endpoint, body) {
    return json(`${root}cart/${endpoint}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    });
  }

  const readCart = () => json(`${root}cart.js`);
  const isGift = (item) => item.properties?._bq_gift === 'true' || reserved.has(String(item.variant_id));

  function target(cart) {
    const giftTotal = cart.items.filter(isGift).reduce((sum, item) => sum + item.final_line_price, 0);
    const spend = Math.max(0, cart.total_price - giftTotal);
    return tiers.find((tier) => spend >= tier.minimum);
  }

  async function reconcile() {
    let cart = await readCart();
    const unavailable = new Set();
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const tier = target(cart);
      const desired = tier && !unavailable.has(String(tier.variantId)) ? tier : undefined;
      const gifts = cart.items.filter(isGift);
      if (desired && gifts.some((item) => String(item.variant_id) === String(desired.variantId) && item.price !== 0)) {
        unavailable.add(String(desired.variantId));
        continue;
      }
      const keeper = desired && gifts.find((item) =>
        String(item.variant_id) === String(desired.variantId) &&
        item.properties?._bq_gift === 'true' && !item.selling_plan_allocation && item.final_price === 0
      );
      const updates = {};
      gifts.forEach((item) => {
        if (item !== keeper) updates[item.key] = 0;
        else if (item.quantity !== 1) updates[item.key] = 1;
      });
      if (Object.keys(updates).length) {
        await post('update', { updates });
        cart = await readCart();
        continue;
      }
      if (desired && !keeper) {
        try {
          await post('add', { items: [{ id: desired.variantId, quantity: 1, properties: { _bq_gift: 'true' } }] });
        } catch (error) {
          if (error.status !== 422) throw error;
          unavailable.add(String(desired.variantId));
        }
        cart = await readCart();
        continue;
      }
      report(null);
      return cart;
    }
    throw new Error(config.error);
  }

  async function sections(ids, requireAll = false) {
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('sections', ids.join(','));
    const result = await json(url.toString());
    if (requireAll && ids.some((id) => typeof result[id] !== 'string')) throw new Error(config.error);
    return result;
  }

  async function refresh(cart) {
    const main = document.querySelector('[data-section-type="cart"]');
    const ids = ['header', 'cart-drawer'];
    if (main) ids.push(main.dataset.sectionId);
    const html = await sections(ids);
    const parser = new DOMParser();
    const drawer = document.querySelector('cart-drawer');
    const drawerSource = html['cart-drawer'] && parser.parseFromString(html['cart-drawer'], 'text/html').querySelector('#CartDrawer');
    if (drawer && drawerSource) {
      drawer.classList.toggle('is-empty', cart.item_count === 0);
      document.getElementById('CartDrawer').innerHTML = drawerSource.innerHTML;
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      drawer.initDiscountCode();
      drawer.initProductCarousel();
      if (drawer.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(drawer.querySelector('.drawer__inner'), drawer.querySelector('.drawer__close'));
      }
    }
    if (main && html[main.dataset.sectionId]) {
      const source = parser.parseFromString(html[main.dataset.sectionId], 'text/html').querySelector('[data-section-type="cart"]');
      if (source) {
        if (main.querySelector('form') && source.querySelector('form')) {
          ['.cart-items', '.subtotal-row'].forEach((selector) => {
            const current = main.querySelector(selector);
            const next = source.querySelector(selector);
            if (current && next) current.innerHTML = next.innerHTML;
          });
        } else main.innerHTML = source.innerHTML;
      }
    }
    if (html.header) {
      const header = parser.parseFromString(html.header, 'text/html');
      ['.page-header .cart-summary', '.docked-navigation-container .cart-summary'].forEach((selector) => {
        const source = header.querySelector(selector);
        const current = document.querySelector(selector);
        if (source && current) current.innerHTML = source.innerHTML;
      });
    }
    document.querySelectorAll('.header-cart__count').forEach((element) => { element.textContent = cart.item_count; });
    renderedSignature = signature(cart);
    document.dispatchEvent(new CustomEvent('bq:gift-cart-updated', { detail: { cart } }));
  }

  async function request(url, options = {}) {
    return enqueue(async () => {
      const response = await fetch(url, options);
      if (!response.ok) {
        try {
          const cart = await reconcile();
          if (signature(cart) !== renderedSignature) await refresh(cart);
        } catch (error) { report(error); }
        return response;
      }
      const data = await response.clone().json();
      let cart;
      try {
        cart = await reconcile();
      } catch (error) {
        report(error);
        throw error;
      }
      if (data.sections) data.sections = await sections(Object.keys(data.sections), true);
      if (Array.isArray(data.items) && typeof data.total_price === 'number') {
        Object.assign(data, cart);
      }
      document.querySelectorAll('.header-cart__count').forEach((element) => { element.textContent = cart.item_count; });
      return new Response(JSON.stringify(data), { status: response.status, headers: { 'Content-Type': 'application/json' } });
    }).catch((error) => {
      report(error);
      throw error;
    });
  }

  function ajax(options) {
    const deferred = window.jQuery.Deferred();
    const body = typeof options.data === 'string' ? options.data : new URLSearchParams(options.data).toString();
    request(options.url, {
      method: options.type || 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }, body
    }).then(async (response) => {
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(data.description || config.error);
        error.status = response.status;
        error.responseJSON = data;
        throw error;
      }
      options.success?.(data);
      deferred.resolve(data);
    }).catch((error) => {
      const failure = { status: error.status || 503, statusText: 'error', responseJSON: error.responseJSON || { description: config.error } };
      options.error?.(failure);
      deferred.reject(failure);
    }).finally(() => options.complete?.());
    const promise = deferred.promise();
    promise.abort = () => {};
    return promise;
  }

  function sync(render = true) {
    return enqueue(async () => {
      try {
        const cart = await reconcile();
        if (render && signature(cart) !== renderedSignature) await refresh(cart);
        return cart;
      } catch (error) {
        report(error);
        throw error;
      }
    });
  }

  async function checkout(form, submitter, href) {
    if (checkoutBusy) return;
    checkoutBusy = true;
    try {
      await new Promise((resolve) => setTimeout(resolve, 650));
      const cart = await sync(false);
      if (!cart.item_count) {
        await refresh(cart);
        return;
      }
      if (form) {
        if (!form.isConnected) {
          form = form.id ? document.getElementById(form.id) : document.querySelector('[data-section-type="cart"] form');
          submitter = form?.querySelector('[name="checkout"]') || document.querySelector('[name="checkout"][form="CartDrawer-Form"]');
        }
        if (!form || !submitter) throw new Error(config.error);
        const inputs = [...form.querySelectorAll('[name="updates[]"]')];
        const states = inputs.map((input) => input.disabled);
        inputs.forEach((input) => { input.disabled = true; });
        form.dataset.bqCheckoutReady = 'true';
        try { form.requestSubmit(submitter); }
        finally {
          delete form.dataset.bqCheckoutReady;
          inputs.forEach((input, index) => { input.disabled = states[index]; });
        }
      } else window.location.assign(href);
    } catch (error) { report(error); }
    finally { checkoutBusy = false; }
  }

  window.BQGiftCart = { fetch: request, ajax, sync, refresh, isGift };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.dataset.bqCheckoutReady === 'true') return;
    if (!event.submitter?.matches('[name="checkout"]')) return;
    event.preventDefault();
    if (document.getElementById('terms') && !document.getElementById('terms').checked) return;
    checkout(form, event.submitter);
  }, true);

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-bq-line-key]');
    if (remove && !event.defaultPrevented) {
      event.preventDefault();
      request(`${root}cart/change.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: remove.dataset.bqLineKey, quantity: 0 })
      }).then(async (response) => {
        if (!response.ok) throw new Error(config.error);
        await sync();
      }).catch(report);
      return;
    }
    const link = event.target.closest('a[href]');
    if (!link || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    event.preventDefault();
    if (document.getElementById('terms') && !document.getElementById('terms').checked) return;
    checkout(null, null, link.href);
  });

  const start = () => sync().catch(() => {});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('pageshow', (event) => { if (event.persisted) start(); });
  window.addEventListener('focus', start);
})();
